import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Target, TrendingDown, AlertTriangle, ShieldCheck, Zap } from 'lucide-react';

/**
 * Net Score KPI card — surfaces the UPSC negative-marking simulation
 * from /api/student-strategy/negative-marking
 *
 * Hidden when examType === 'mains' (negative marking is a Prelims/CSAT concept).
 */
const RISK_META = {
  conservative_efficient: {
    label: 'Conservative',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Icon: ShieldCheck,
  },
  balanced: {
    label: 'Balanced',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    Icon: Target,
  },
  aggressive: {
    label: 'Aggressive',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    Icon: Zap,
  },
  over_attempting: {
    label: 'Over-attempting',
    badge: 'bg-red-50 text-red-700 border-red-200',
    Icon: TrendingDown,
  },
  knowledge_gap: {
    label: 'Knowledge gap',
    badge: 'bg-red-50 text-red-700 border-red-200',
    Icon: AlertTriangle,
  },
  insufficient_data: {
    label: 'Not enough data',
    badge: 'bg-slate-50 text-slate-600 border-slate-200',
    Icon: Target,
  },
};

export const NetScoreCard = ({ data, examType }) => {
  // Hide entirely when filter is Mains-only
  if (examType === 'mains') return null;

  if (!data || !data.has_data) {
    return (
      <Card className="border-0 shadow-md" data-testid="net-score-card-empty">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Net Score</span>
            <Target className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-bold text-slate-400">—</div>
          <p className="text-xs text-slate-400 mt-1">Attempt 5+ MCQs to unlock</p>
        </CardContent>
      </Card>
    );
  }

  const { scoring, totals, risk_profile, risk_label } = data;
  const meta = RISK_META[risk_profile] || RISK_META.balanced;
  const Icon = meta.Icon;
  const lost = scoring.marks_lost_to_penalty;
  const accentColor =
    risk_profile === 'over_attempting' || risk_profile === 'knowledge_gap'
      ? 'text-red-500'
      : risk_profile === 'conservative_efficient'
        ? 'text-emerald-500'
        : risk_profile === 'balanced'
          ? 'text-blue-500'
          : 'text-amber-500';

  return (
    <Card className="border-0 shadow-md" data-testid="net-score-card" title={risk_label}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-500">Net Score</span>
          <Icon className={`w-4 h-4 ${accentColor}`} />
        </div>
        <div className="flex items-baseline gap-1">
          <span className={`text-2xl font-bold ${accentColor}`}>{scoring.net_marks}</span>
          <span className="text-sm text-slate-400">/ {scoring.gross_marks}</span>
        </div>
        <div className="flex items-center justify-between mt-2 gap-2">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${meta.badge}`} data-testid="net-score-risk-badge">
            {meta.label}
          </Badge>
          <span className="text-[11px] text-red-500 font-medium" data-testid="net-score-marks-lost">
            −{lost}
          </span>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">
          {totals.correct}✓ · {totals.wrong}✗ on {totals.attempted}q
        </p>
      </CardContent>
    </Card>
  );
};

/**
 * Detailed Net Score panel — expanded view shown below Top Insight
 * in the Overview tab when negative-marking data is available.
 */
export const NetScoreDetailPanel = ({ data, examType }) => {
  if (examType === 'mains' || !data || !data.has_data) return null;

  const { scoring, totals, risk_label, hints, subject_breakdown } = data;
  const top3 = (subject_breakdown || []).slice(0, 3);

  return (
    <Card className="border-0 shadow-md" data-testid="net-score-detail-panel">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-indigo-500" />
          <h3 className="font-semibold text-slate-800">UPSC Negative Marking Simulation</h3>
          <Badge variant="outline" className="ml-auto text-[10px]">+2 / −0.667 / 0</Badge>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Stat label="Gross" value={scoring.gross_marks} tone="text-slate-700" />
          <Stat label="Penalty" value={`−${scoring.marks_lost_to_penalty}`} tone="text-red-500" />
          <Stat label="Net" value={scoring.net_marks} tone="text-indigo-600" big />
          <Stat label="If skipped wrong" value={scoring.blank_alternative_score} tone="text-emerald-600" />
        </div>

        <p className="text-xs text-slate-500 mb-3">
          <span className="font-medium text-slate-700">{risk_label}.</span>{' '}
          {totals.correct} correct, {totals.wrong} wrong on {totals.attempted} questions
          {' '}({totals.accuracy_percent}% accuracy).
        </p>

        {hints && hints.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-900 leading-relaxed">{hints[0]}</p>
          </div>
        )}

        {top3.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-600 mb-2">Subjects costing you the most</p>
            <div className="space-y-1.5">
              {top3.map((s) => (
                <div
                  key={s.subject}
                  className="flex items-center justify-between text-xs bg-slate-50 rounded-md px-3 py-2"
                  data-testid={`net-score-subject-${s.subject}`}
                >
                  <span className="font-medium text-slate-700 capitalize">{s.subject.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-slate-500">{s.attempted}q · {s.accuracy}%</span>
                    <span className="text-red-500 font-medium">−{s.marks_lost_to_penalty}</span>
                    <span className="text-slate-700 font-semibold">{s.net_marks}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, tone, big }) => (
  <div className="bg-slate-50 rounded-lg p-2.5">
    <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">{label}</p>
    <p className={`font-bold ${tone} ${big ? 'text-xl' : 'text-base'}`}>{value}</p>
  </div>
);
